/**
 * Microsoft Graph route contracts (design 5.3.1): one endpoint per
 * frontend-consumed (method, path). The worker registers the integration
 * CRUD handlers through these (worker/src/features/msgraph/api.ts); the
 * frontend client (frontend/src/features/msgraph/client.js) invokes the
 * same objects. Same-path pairs (GET + PUT /api/integrations/msgraph,
 * GET + DELETE /api/msgraph/consent/binding) are contracted separately —
 * contracts are method-aware.
 *
 * The four consent contracts are deliberately NOT worker-registered:
 * /api/msgraph/consent is un-licensed in the pre-migration gate baseline
 * (featureForPath → null — it must stay reachable to hand consent state
 * back after the Microsoft redirect), and a module registration can only
 * attribute a carried key, which would flip the gate to 'msgraph'. The
 * consent lanes keep flowing through the frozen legacy dispatch in
 * routes/handler.ts (via the routes/msgraph-consent.ts shim); these
 * contract objects exist so the frontend builds the paths without raw
 * literals. The consent lanes are PLATFORM-owned contracts (decided-free
 * handshake surface — see shared/features/platform.contracts.ts).
 */

import { defineContracts, endpoint } from '../featureModule'
import { identity } from './msgraph'

export const contracts = defineContracts(identity, {
  /** GET /api/integrations/msgraph — the msgraph integration row (config redacted). */
  getIntegration: endpoint('GET', '/api/integrations/msgraph', { entitlement: 'msgraph' }),
  /** PUT /api/integrations/msgraph — update credentials/enabled/auth_mode. */
  updateIntegration: endpoint('PUT', '/api/integrations/msgraph', { entitlement: 'msgraph' }),
  /** POST /api/integrations/msgraph/test — Graph connectivity probe. */
  testIntegration: endpoint('POST', '/api/integrations/msgraph/test', { entitlement: 'msgraph' }),


  /**
   * GET /api/ai/graph-specs/status — applied Microsoft Graph release + RAG
   * status for the AI Governor Knowledge Sources panel (ai_providers imports
   * this cross-feature; the gate stays msgraph). Sync is centrally ingested
   * (POST 410), so only the status read is contracted; the module's
   * '/api/ai/graph-specs' prefix still serves the rest.
   */
  graphSpecsStatus: endpoint('GET', '/api/ai/graph-specs/status', { entitlement: 'msgraph' }),

  // ── AI agent access (plan_25b45738) ────────────────────────────────────
  /**
   * POST /api/msgraph/agent-areas { agentAssertions } — the tenant's areas
   * with this person's status (from the click's sign-ins, carried in the
   * body), Microsoft's blocked list and the mode. Served by the
   * mcp-knows-microsoft-areas read path, which MCP reads too; System
   * settings → Microsoft Graph renders it. A POST because the sign-ins of
   * every area do not fit a header.
   */
  getAgentAreas: endpoint('POST', '/api/msgraph/agent-areas', { entitlement: 'msgraph' }),
  /**
   * POST /api/msgraph/agent-areas/validate — { area, agentAssertions } → the
   * person's own agent for that area checked live, step by step (sign-in,
   * approval, agent, permissions, one harmless read).
   */
  validateAgentArea: endpoint('POST', '/api/msgraph/agent-areas/validate', { entitlement: 'msgraph' }),
  /** GET /api/msgraph/summary — the Microsoft 365 page's counts: { peopleWithAgents, areaAgents }. */
  getMicrosoft365Summary: endpoint('GET', '/api/msgraph/summary', { entitlement: 'msgraph' }),
  /**
   * GET /api/msgraph/people — People & agents: every person with area agents,
   * each agent with its area, status and last call. ops.msgraph.people() reads the same.
   */
  getAgentPeople: endpoint('GET', '/api/msgraph/people', { entitlement: 'msgraph' }),
  /**
   * GET /api/msgraph/activity?person=&area=&lane=&cursor=&limit= — the recorded
   * agent calls, newest first, one page: { calls, nextCursor }. ops.msgraph.activity() reads the same.
   */
  getAgentActivity: endpoint('GET', '/api/msgraph/activity', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/people/remove { personOid } → { removed }: every area agent of that person, removed. */
  removePersonAreaAgents: endpoint('POST', '/api/msgraph/people/remove', { entitlement: 'msgraph' }),
  /** GET /api/msgraph/agent-areas/details — the catalogue with Microsoft's description of every permission in it. */
  getAgentAreaDetails: endpoint('GET', '/api/msgraph/agent-areas/details', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/agent-areas/consent — { area, step: 'blueprint' | 'console' } → { consentUrl }. */
  startAgentAreaConsent: endpoint('POST', '/api/msgraph/agent-areas/consent', { entitlement: 'msgraph' }),
  /** GET /api/msgraph/bring-your-own — what command one needs, and the tenant's own registration: { source, sha, consoleClientId, areas, own }. */
  getBringYourOwn: endpoint('GET', '/api/msgraph/bring-your-own', { entitlement: 'msgraph' }),
  /**
   * POST /api/msgraph/bring-your-own/validate — { link?, agentAssertions, switch? }
   * → { passed, switched, checks }: the five checks against the link's broker
   * (or the tenant's current own broker), switching only when every one passes.
   */
  validateBringYourOwn: endpoint('POST', '/api/msgraph/bring-your-own/validate', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/bring-your-own/shared — switch back to GAEZLA's shared blueprints. */
  switchToSharedBlueprints: endpoint('POST', '/api/msgraph/bring-your-own/shared', { entitlement: 'msgraph' }),

  // ── Email: the organization's shared mailbox (plan the-ai-sends-and-reads-mail-as-the-organization) ──
  /**
   * GET /api/msgraph/email — the email channel: the mailbox, the allowlists,
   * the footer switch, the unattended identity, the generated Exchange
   * commands and the mailbox's activation status. ops.msgraph.email() reads the same.
   */
  getEmailChannel: endpoint('GET', '/api/msgraph/email', { entitlement: 'msgraph' }),
  /** PUT /api/msgraph/email — { mailbox?, writeTo?, actOnFrom?, footer? } → the email view; registers the mailbox with the admin. */
  updateEmailChannel: endpoint('PUT', '/api/msgraph/email', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/email/validate — checks the mailbox now; activation keeps being checked in the background. → the email view. */
  validateEmailChannel: endpoint('POST', '/api/msgraph/email/validate', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/email/consent — { } → { consentUrl }: Microsoft's screen that adds the mail app (granting nothing). */
  startMailAppConsent: endpoint('POST', '/api/msgraph/email/consent', { entitlement: 'msgraph' }),
  /** POST /api/msgraph/email/broker-identity — { link }: the bring-your-own mail step's link; records the broker identity. → the email view. */
  recordMailBrokerIdentity: endpoint('POST', '/api/msgraph/email/broker-identity', { entitlement: 'msgraph' }),
})
